import axios from 'axios';
import { ValidationResults, SpecType, LLMSettings, LLMJudgeMetrics, MappingResults, UnmappedSourceFieldEntry } from '../types';
import { getTokens } from '../authentication/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function unmappedSourceFieldName(field: UnmappedSourceFieldEntry): string {
  return typeof field === 'string' ? field : field.field;
}

apiClient.interceptors.request.use((config) => {
  const { accessToken } = getTokens();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

function toError(reason: unknown): Error {
  const responseMessage = (reason as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return new Error(responseMessage);
  }

  if (reason instanceof Error) {
    return reason;
  }

  if (typeof reason === 'string') {
    return new Error(reason);
  }

  return new Error('API request failed');
}

// Do NOT log request/response bodies if they may contain PHI or sensitive data.
apiClient.interceptors.response.use(
  response => response,
  error => {
    return Promise.reject(toError(error));
  }
);

export const api = {
  async validateFile(file: File, specType: SpecType): Promise<ValidationResults> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('specType', specType);
    
    const response = await apiClient.post<ValidationResults>('/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  
  async getSpecifications(): Promise<Record<SpecType, { name: string; version: string; recordLength: number; records: string[] }>> {
    const response = await apiClient.get('/specifications');
    return response.data;
  },
  
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  async checkGuardrails(text: string): Promise<{ passed: boolean; category: string; message: string }> {
    const response = await apiClient.post('/guardrails/check', { text });
    return response.data;
  },

  async judgeMapping(mappingResults: Partial<MappingResults>, options?: { consensusRuns?: number }): Promise<LLMJudgeMetrics> {
    const response = await apiClient.post<LLMJudgeMetrics>('/guardrails/judge/mapping', {
      mappings: mappingResults.mappings || [],
      unmappedSourceFields: (mappingResults.unmappedSourceFields || []).map(unmappedSourceFieldName),
      unmappedTargetFields: mappingResults.unmappedTargetFields || [],
      sourceFormat: mappingResults.sourceFormat || 'Unknown',
      targetSpec: mappingResults.targetSpec || 'HPS',
      ...(options?.consensusRuns ? { consensusRuns: options.consensusRuns } : {}),
    });
    return response.data;
  },

  async judgeValidation(summary: {
    totalRecords: number;
    validRecords: number;
    errors: unknown[];
    warnings: unknown[];
  }): Promise<LLMJudgeMetrics> {
    const response = await apiClient.post<LLMJudgeMetrics>('/guardrails/judge/validation', summary);
    return response.data;
  },

  async validateFileWithSettings(file: File, specType: SpecType, _llmSettings?: LLMSettings): Promise<ValidationResults> {
    // Validation is rule-based; llmSettings reserved for future use
    return this.validateFile(file, specType);
  },
};

export default api;
